import { Component } from 'react'
import styled from '@emotion/styled'
import { T, __, __UP } from 'stylewhere/i18n'
import { ErrorLogs, Shippings } from 'stylewhere/api'
import { Router, RemoteOperation, OperationReadingState, OperationReadingProps, AppStore } from 'stylewhere/shared'
import { ConveyorExtensions, ShippingExtensions } from 'stylewhere/extensions'
import { ShippingOperationConfig } from 'stylewhere/shared/RemoteOperation'
import {
  Box,
  Button,
  Text,
  Icons,
  Page,
  TagCounter,
  ErrorLog,
  Stoplight,
  OperationReadingList
} from 'stylewhere/components'
import {
  getCustomTranslation,
  KEEP_ALIVE_CONTROL_TIME,
  RETRY_SSE_TIME,
  MAX_LOG_ERROR,
  CONVEYOR_MIN_WIDTH_SCREEN,
  ERROR_DATE_FORMAT,
  hasChecklist,
  getChecklistItemsCount,
  askUserConfirmation,
  showToast,
  isModalError
} from 'stylewhere/shared/utils'
import { config } from 'stylewhere/config'
import { format } from 'date-fns'

interface State extends OperationReadingState {
  status: number
  errorLogs: ErrorLogs[]
  isMobileVersion: boolean
  sseStatus: boolean
  retrySseCount: number
  detected: number
  expected: number
  unexpected: number
  missing: number
  barcode: string
  parcel?: any // ShippingParcel compatibile
  isChecklist: boolean
}

export default class ConveyorReading extends Component<OperationReadingProps<State>, State> {
  matchParams = Router.getMatchParams(this.props)
  locationState = Router.getLocationState<State>(this.props)
  operation = RemoteOperation.getOperationConfig<ShippingOperationConfig>(this.matchParams.opCode)
  formSchema = ShippingExtensions.formSchema(this.operation, {})
  keepAliveTimer: NodeJS.Timeout | undefined
  sse: any
  isModal = false

  state: State = {
    items: [],
    loading: true,
    formData: this.locationState.formData ?? {},
    status: 0,
    errorLogs: [],
    isMobileVersion: window.innerWidth < CONVEYOR_MIN_WIDTH_SCREEN,
    sseStatus: false,
    retrySseCount: 0,
    detected: 0,
    expected: 0,
    unexpected: 0,
    missing: 0,
    barcode: '---',
    parcel: undefined,
    isChecklist: false
  }

  componentDidMount = () => {
    if (!this.locationState.formData?.conveyor) {
      this.goBack()
    } else {
      this.initializeConveyor(this.locationState.formData.conveyor.id, this.operation)
      this.isModal = isModalError(this.operation)
      this.attachSse()
      this.handleResize()
      window.addEventListener('resize', this.handleResize)
    }
  }

  initializeConveyor = async (conveyorId: string, operation: ShippingOperationConfig) => {
    ConveyorExtensions.initializeConveyor(conveyorId, operation)
  }

  handleResize = () => {
    const { innerWidth: width } = window
    this.setState({ isMobileVersion: width < CONVEYOR_MIN_WIDTH_SCREEN })
  }

  componentWillUnmount = () => {
    this.setState({
      barcode: '---',
      detected: 0,
      expected: 0,
      unexpected: 0,
      missing: 0,
      items: [],
      status: 0,
      errorLogs: [],
      parcel: undefined,
      isChecklist: false
    })
    this.destroySse()
  }

  attachSse = async () => {
    const conveyorId = this.locationState.formData.conveyor.id || this.locationState.formData.conveyor
    const sseUrl =
      config.endpoint + '/api/v1/sse/getData/conveyor/' + conveyorId + '?access_token=' + AppStore.authToken
    this.sse = new EventSource(sseUrl, { withCredentials: false })
    this.sse.onerror = () => {
      this.setState(
        (prevState) => ({
          sseStatus: false,
          retrySseCount: prevState.retrySseCount + 1
        }),
        this.retrySse
      )
    }
    this.sse.onopen = (event) => {
      this.setState({ sseStatus: true, retrySseCount: 0 })
    }
    this.sse.onmessage = (event) => {
      this.sseEventMessage(event)
    }
    this.sse.addEventListener('keep-alive', (e) => {
      this.startKeepAliveTimer()
    })
  }

  retrySse = () => {
    this.destroySse()
    if (this.state.retrySseCount === 1) {
      this.attachSse()
    } else {
      setTimeout(() => {
        this.attachSse()
      }, RETRY_SSE_TIME)
    }
  }

  startKeepAliveTimer = () => {
    this.clearKeepAliveTimer()
    this.keepAliveTimer = setTimeout(() => {
      this.setState(
        (prevState) => ({
          sseStatus: false,
          retrySseCount: prevState.retrySseCount + 1
        }),
        this.retrySse
      )
    }, KEEP_ALIVE_CONTROL_TIME)
  }

  clearKeepAliveTimer = () => {
    if (this.keepAliveTimer) {
      clearTimeout(this.keepAliveTimer)
      this.keepAliveTimer = undefined
    }
  }

  getBoxStatus = (tunnelEventType) => {
    let status = 2 // RFID_START_READING
    if (tunnelEventType === 'RFID_STOP_READING') {
      status = 3
    } else if (tunnelEventType === 'BARCODE_READ' || tunnelEventType === 'ELABORATION_COMPLETED') {
      status = 1
    } else if (tunnelEventType === 'RESULT_EVENT') {
      status = 4
    }
    return status
  }

  /* 
    logLevel: TRACE, INFO, WARNING, ERROR
    tunnelEventType: BARCODE_READ, RFID_START_READING, RFID_STOP_READING, RESULT_EVENT
  */
  handleElaborationCompleted = (eventData, tmp, tmpStatus) => {
    const payload = eventData.data.elaborationResult.payload
    if (payload?.products && payload.checklist) {
      this.setState({ isChecklist: true })
      const { expecteds } = getChecklistItemsCount(
        [],
        this.operation,
        ShippingExtensions,
        payload.checklist,
        payload.products
      )
      const log = {
        date: eventData.data.eventCreationDate
          ? format(new Date(eventData.data.eventCreationDate), ERROR_DATE_FORMAT)
          : format(new Date(), ERROR_DATE_FORMAT),
        status: 'success',
        message: getCustomTranslation('BARCODE_READ')
      }
      tmp.splice(0, 0, log)
      if (tmp.length > MAX_LOG_ERROR) tmp.pop()
      this.setState({
        errorLogs: tmp,
        items: [],
        status: tmpStatus,
        barcode: payload?.code || eventData.data.barcode,
        parcel: payload,
        expected: expecteds,
        detected: 0,
        unexpected: 0,
        missing: 0
      })
    } else if (
      eventData.data.elaborationResult.errors &&
      eventData.data.elaborationResult.errors.length > 0 &&
      eventData.data.elaborationResult.errors[0].errorCode &&
      (eventData.data.elaborationResult.errors[0].errorCode.indexOf('INBOUND') !== -1 ||
        eventData.data.elaborationResult.errors[0].errorCode.indexOf('OUTBOUND') !== -1)
    ) {
      let errorMessage = eventData.data.elaborationResult.errors[0].errorCode ?? __(T.error.box_already_received)
      if (errorMessage === 'Cannot start INBOUND from status RECEIVED') {
        errorMessage = __(T.error.box_already_received)
      }
      const log = {
        date: eventData.data.eventCreationDate
          ? format(new Date(eventData.data.eventCreationDate), ERROR_DATE_FORMAT)
          : format(new Date(), ERROR_DATE_FORMAT),
        status: 'error',
        message: errorMessage
      }
      tmp.splice(0, 0, log)

      this.setState({
        errorLogs: tmp,
        items: [],
        status: this.state.status === 1 ? 3 : tmpStatus,
        barcode: payload?.code || eventData.data.barcode,
        parcel: undefined,
        expected: 0,
        detected: 0,
        unexpected: 0,
        missing: 0
      })
    }
  }

  handleRfidStopReading = (eventData, tmp, tmpStatus) => {
    const { parcel } = this.state
    if (parcel) {
      const data = eventData.data
      const identifiers = data.identifiers || []
      const readItems = identifiers.map((identifier) => ({
        identifierCode: identifier.epc,
        item: identifier.item,
        product: identifier.item?.product
      }))
      const { detecteds, expecteds, unexpecteds, missings } = getChecklistItemsCount(
        readItems,
        this.operation,
        ShippingExtensions,
        this.state.parcel?.checklist,
        this.state.parcel?.products
      )
      const log = {
        date: eventData.data.eventCreationDate
          ? format(new Date(eventData.data.eventCreationDate), ERROR_DATE_FORMAT)
          : format(new Date(), ERROR_DATE_FORMAT),
        status: eventData.data.logLevel ? eventData.data.logLevel : 'TRACE',
        message: eventData.data.tunnelEventType ? getCustomTranslation(eventData.data.tunnelEventType) : 'n/a'
      }
      tmp.splice(0, 0, log)
      if (tmp.length > MAX_LOG_ERROR) tmp.pop()
      this.setState({
        errorLogs: tmp,
        detected: detecteds,
        expected: expecteds,
        unexpected: unexpecteds,
        missing: missings,
        items: readItems,
        status: tmpStatus
      })
    }
  }

  sseEventMessage = (event) => {
    const tmp = this.state.errorLogs
    const eventData = event.data ? JSON.parse(event.data) : undefined
    if (eventData?.data) {
      const tmpStatus = this.getBoxStatus(eventData.data.tunnelEventType || '')
      if (eventData.data.tunnelEventType === 'ELABORATION_COMPLETED') {
        this.handleElaborationCompleted(eventData, tmp, tmpStatus)
      }
      if (eventData.data.tunnelEventType === 'RFID_STOP_READING') {
        this.handleRfidStopReading(eventData, tmp, tmpStatus)
      }
    }
  }

  getStatusData = () => {
    const { status } = this.state
    if (status === 1 || status === 2) {
      return {
        background: '#FFF',
        label: __UP(__(T.misc.reading))
      }
    } else if (status === 3) {
      // RFID_STOP_READING
      return {
        background: '#FFF', //'#F27D7D',
        label: __UP(__(T.misc.box_out))
      }
    } else if (status === 4) {
      // RESULT_EVENT
      return {
        background: '#FFF', //'#F27D7D',
        label: __UP(__(T.misc.awaiting))
      }
    }
    // DEFAULT
    return {
      background: '#FFF', //'#EBB575',
      label: __UP(__(T.misc.awaiting))
    }
  }

  destroySse = () => {
    if (this.sse) {
      this.sse.close()
      this.sse = undefined
    }
  }

  goBack = () => {
    this.destroySse()
    if (this.formSchema.length) {
      Router.navigate('/shipping/conveyor/:opCode', { opCode: this.operation.code })
    } else {
      Router.navigate('/')
    }
  }

  clearErrorLogs = () => {
    this.setState({ errorLogs: [] })
  }

  overrideProductInfoFields: () => any[] | undefined = () => {
    return undefined
  }

  getHideIdentifierZones: () => boolean = () => {
    return false
  }

  getHideIdentifierCodeByType = (): any[] | undefined => {
    return undefined
  }

  clear = () => {
    this.setState({
      barcode: '---',
      detected: 0,
      expected: 0,
      unexpected: 0,
      missing: 0,
      items: [],
      status: 0,
      errorLogs: [],
      parcel: undefined,
      isChecklist: false
    })
  }

  confirm = async () => {
    const { parcel, items } = this.state

    const askConfirmResult = await askUserConfirmation(
      __(T.custom.force_parcel),
      __(T.custom.force_parcel_message),
      __(T.misc.no),
      __(T.misc.yes)
    )
    if (askConfirmResult) {
      const confirmParcelData: any = {
        operationId: this.operation.id,
        parcelCode: parcel.code
      }
      const result: any = await Shippings.confirmParcel(
        this.operation,
        confirmParcelData,
        true, // set force receive
        false,
        !!parcel.asn
      )
      if (result) {
        this.clear()
        showToast({
          title: __(T.misc.success),
          description: __(T.messages.parcel_success),
          status: 'success'
        })
      }
    }
  }

  renderSidebar(statusData, counterLayout) {
    const { detected, expected, unexpected, isChecklist, errorLogs, isMobileVersion, missing } = this.state
    return (
      <Page.Sidebar
        width={isMobileVersion ? '100%' : 500}
        pl={0}
        pb={0}
        height={'100%'}
        style={{ overflowY: 'hidden' }}
      >
        <Box flex style={{ overflowY: 'hidden' }}>
          <Box row ml={15} mr={15}>
            <TagCounter
              title={__UP(__(T.misc.total_reads))}
              layout={counterLayout}
              margin={'0px 3px 0px 0px'}
              detected={detected}
              expected={isChecklist ? expected : undefined}
              unexpected={isChecklist ? unexpected : undefined}
            />
          </Box>
          <Box height={40} row bg="white" borderRadius={10} ml={15} mr={15} vcenter center mt={6} mb={0}>
            <Text style={{ flex: 1, textAlign: 'right' }}>{__UP(__(T.misc.status))}</Text>
            <Status bg={statusData.background}>{statusData.label}</Status>
          </Box>
          <Box height={40} row bg="white" borderRadius={10} ml={15} mr={15} vcenter center mt={6} mb={10}>
            <Text style={{ flex: 1, textAlign: 'right' }}>{__UP(__(T.custom.BARCODE_READ))}</Text>
            <Status bg={statusData.background}>{this.state.barcode}</Status>
          </Box>
          {!isMobileVersion && (
            <Box flex mt={0} height={'calc(100% - 286px)'}>
              <Box row vcenter>
                <Text style={{ marginRight: 15, marginLeft: 15 }} bold fontSize={20}>
                  {__(T.misc.event_logs)}
                </Text>
                <Button
                  disabled={errorLogs.length === 0}
                  padding={'0px'}
                  variant="default"
                  circle
                  onClick={this.clearErrorLogs}
                  size="small"
                >
                  <Icons.Clear width={20} height={20} />
                </Button>
              </Box>
              <Box flex hideBar height={'calc(100% - 36px)'} style={{ overflowY: 'auto' }}>
                <Box p={10} pb={0} flex bg="white" mt={10} mr={5} ml={5}>
                  {errorLogs.map((log, index) => (
                    <ErrorLog onlyMessage key={'error-log-' + index} log={log} />
                  ))}
                </Box>
              </Box>
            </Box>
          )}
        </Box>
        {(unexpected > 0 || missing > 0 || this.state.status === 3) && (
          <Box mt={10} mb={10} ml={15} mr={15}>
            <Box flex row>
              <Button style={{ flex: 1, marginRight: 5 }} variant="default" onClick={() => this.clear()}>
                {__UP(__(T.misc.clear))}
              </Button>
              {(unexpected > 0 || missing > 0) && (
                <Button style={{ flex: 1, marginLeft: 5 }} onClick={() => this.confirm()}>
                  {__UP(__(T.misc.confirm))}
                </Button>
              )}
            </Box>
          </Box>
        )}
      </Page.Sidebar>
    )
  }

  renderContent() {
    const { items, parcel, isMobileVersion } = this.state
    return (
      <Page.Content notBoxed style={{ margin: 0, marginLeft: 0 }}>
        {!parcel ? (
          <Box borderRadius={0} ph={0} flex bg="white" center>
            <Text center style={{ width: 500 }} fontSize={30}>
              {__(T.messages.reading_in_progress)}...
            </Text>
          </Box>
        ) : (
          <Box flex mr={isMobileVersion ? 0 : 15} mt={isMobileVersion ? 0 : 15} mb={isMobileVersion ? 0 : 15}>
            <OperationReadingList
              extension={ShippingExtensions}
              items={items}
              operation={this.operation}
              checklist={parcel && hasChecklist(this.operation) ? parcel.checklist : undefined}
              products={parcel && hasChecklist(this.operation) ? parcel.products : undefined}
              overrideProductInfoFields={this.overrideProductInfoFields()}
              hideIdentifiersZones={this.getHideIdentifierZones()}
              hideIdentifiersByType={this.getHideIdentifierCodeByType()}
              removeModeEnabled={false}
            />
          </Box>
        )}
      </Page.Content>
    )
  }

  render() {
    const { formData, loading, sseStatus } = this.state
    const statusData = this.getStatusData()
    const counterLayout = {
      disableSpacer: true,
      height: 180,
      size: '90px',
      interline: '100px',
      sizeText: '18px',
      lineHeightText: '22px'
    }

    return (
      <Page
        title={this.operation.description}
        onBackPress={() => this.goBack()}
        loading={loading}
        header={{
          details: {
            data: formData,
            formSchema: this.formSchema
          }
        }}
        headerRight={<Stoplight active={sseStatus} />}
        enableEmulation
        contentWrapperColumn={this.state.isMobileVersion}
      >
        {this.renderSidebar(statusData, counterLayout)}
        {this.renderContent()}
      </Page>
    )
  }
}

const Status = styled.div<{ bg: string }>`
  background: ${({ bg }) => bg || '#F27D7D;'};
  margin-left: 10px;
  font-weight: bold;
  border-radius: 5px;
  padding: 0px 5px;
  flex: 1;
`
